import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Send, FileCode2, Landmark } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { mydataApi, mydataError } from "@/api";
import { useLang, fmtMoney, fmtDate, fmtDateTime } from "@/i18n";
import { MyDataBadge } from "@/components/MyDataBadge";
import { IncomingDocs } from "@/components/IncomingDocs";
import { MyDataStatusBanner } from "@/components/MyDataStatusBanner";
import { cn } from "@/lib/utils";

const now = new Date();
const YEARS = [now.getFullYear() - 1, now.getFullYear()];

const Stat = ({ label, value, testId, tone = "" }) => (
  <div className="erp-card p-4" data-testid={testId}>
    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className={cn("mt-1 text-2xl font-mono font-medium", tone)}>{value}</div>
  </div>
);

export default function MyData() {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [log, setLog] = useState([]);
  const [payroll, setPayroll] = useState([]);
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    mydataApi.overview().then(setData).catch(() => toast.error(t("error")));
    mydataApi.log().then(setLog);
    mydataApi.payroll().then(setPayroll);
  }, [t]);
  useEffect(load, [load]);

  const monthName = (m) => new Date(2000, m - 1, 1).toLocaleDateString(lang === "el" ? "el-GR" : "en-GB", { month: "long" });

  const sendOne = async (d) => {
    try {
      const res = await mydataApi.send(d.id, "1.1");
      if (res.mydata.status === "error") toast.error(`${d.number}: ${res.mydata.errors?.[0]?.message || t("mydata_error")}`);
      else toast.success(`${d.number}: ${t("mydata_sent_ok")}`);
    } catch (e) { toast.error(`${d.number}: ${mydataError(e, t)}`); }
  };
  const sendAll = async () => {
    setBusy(true);
    for (const d of data.pending_sales) await sendOne(d); // eslint-disable-line no-await-in-loop
    setBusy(false); load();
  };
  const sendPayroll = async () => {
    setBusy(true);
    try {
      const res = await mydataApi.sendPayroll(Number(year), Number(month));
      if (res.submission.status === "error") toast.error(`${t("mydata_error")}: ${res.submission.errors?.[0]?.message || ""}`);
      else toast.success(t("mydata_sent_ok"));
      load();
    } catch (e) { toast.error(mydataError(e, t)); } finally { setBusy(false); }
  };

  if (!data) return <div className="text-slate-500 text-sm">…</div>;

  return (
    <div className="space-y-6 page-enter" data-testid="mydata-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight">{t("mydata_title")}</h1>
          <p className="text-sm text-slate-500 mt-1 font-mono">{data.settings.base_url} · {t(`mode_${data.settings.mode}`)}</p>
        </div>
        <Link to="/settings" className="text-sm text-blue-700 dark:text-blue-300 hover:underline" data-testid="mydata-settings-link">{t("nav_settings")} →</Link>
      </div>

      <MyDataStatusBanner mode={data.settings.mode} auth={data.auth} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger">
        <Stat label={`${t("doc_invoice")} · ${t("sent")}`} value={`${data.sales_sent}/${data.sales_total}`} testId="mydata-stat-sales-sent" tone="text-emerald-700 dark:text-emerald-300" />
        <Stat label={`${t("doc_invoice")} · ${t("pending")}`} value={data.pending_sales.length} testId="mydata-stat-sales-pending" tone={data.pending_sales.length ? "text-amber-700 dark:text-amber-300" : ""} />
        <Stat label={t("errors")} value={data.sales_errors} testId="mydata-stat-errors" tone={data.sales_errors ? "text-red-600" : ""} />
        <Stat label={`${t("doc_purchase")} · ${t("classified")}`} value={`${data.purchases_classified}/${data.purchases_total}`} testId="mydata-stat-purchases" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="erp-card overflow-hidden" data-testid="pending-sales-card">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-800">
            <h3 className="font-heading font-semibold">{t("pending_sales")} · {data.pending_sales.length}</h3>
            {data.pending_sales.length > 0 && (
              <Button size="sm" disabled={busy} onClick={sendAll} className="bg-blue-800 hover:bg-blue-700" data-testid="mydata-send-all-button"><Send size={14} className="mr-1" />{t("send_all")}</Button>
            )}
          </div>
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {data.pending_sales.map((d) => (
              <div key={d.id} className="px-5 py-3 flex items-center justify-between gap-3 text-sm" data-testid={`pending-sale-${d.id}`}>
                <div className="min-w-0">
                  <Link to={`/documents/${d.id}`} className="font-mono font-medium text-blue-800 dark:text-blue-300 hover:underline">{d.number}</Link>
                  <span className="text-slate-500 ml-2">{d.contact_name}</span>
                  {d.mydata?.status === "error" && <div className="text-xs text-red-600 font-mono truncate">{d.mydata.errors?.[0]?.message}</div>}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-mono">{fmtMoney(d.total)}</span>
                  <Button size="sm" variant="outline" disabled={busy} onClick={async () => { await sendOne(d); load(); }} data-testid={`pending-send-${d.id}`}><Send size={13} /></Button>
                </div>
              </div>
            ))}
            {data.pending_sales.length === 0 && <div className="px-5 py-10 text-center text-sm text-slate-500" data-testid="pending-sales-empty">{t("no_results")}</div>}
          </div>
        </div>

        <div className="space-y-6">
          <div className="erp-card overflow-hidden" data-testid="mydata-payroll-card">
            <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800">
              <h3 className="font-heading font-semibold">{t("mydata_payroll")}</h3>
              <p className="text-xs text-slate-500 mt-1">{t("mydata_payroll_hint")}</p>
            </div>
            <div className="px-5 py-4 flex flex-wrap gap-2 items-center">
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger className="w-28 font-mono" data-testid="mydata-payroll-year-select"><SelectValue /></SelectTrigger>
                <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger className="w-40" data-testid="mydata-payroll-month-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <SelectItem key={m} value={String(m)}>{monthName(m)}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" disabled={busy} onClick={sendPayroll} className="bg-blue-800 hover:bg-blue-700" data-testid="mydata-payroll-send-button"><Landmark size={14} className="mr-1" />{t("mydata_payroll_send")}</Button>
            </div>
            {payroll.length > 0 && (
              <table className="w-full text-sm erp-table border-t border-slate-200 dark:border-slate-800">
                <thead><tr className="text-left"><th className="px-5 py-2">{t("period")}</th><th className="px-5 py-2 text-right">{t("gross")}</th><th className="px-5 py-2 text-right hidden sm:table-cell">{t("employer_contributions")}</th><th className="px-5 py-2">{t("status")}</th><th className="px-5 py-2">{t("mark")}</th></tr></thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {payroll.map((p) => (
                    <tr key={p.id} data-testid={`mydata-payroll-row-${p.year}-${p.month}`}>
                      <td className="px-5 py-2 font-mono">{p.year}-{String(p.month).padStart(2, "0")} <span className="text-slate-400">({p.payslips})</span></td>
                      <td className="px-5 py-2 text-right font-mono">{fmtMoney(p.gross)}</td>
                      <td className="px-5 py-2 text-right font-mono hidden sm:table-cell">{fmtMoney(p.employer_contributions)}</td>
                      <td className="px-5 py-2"><MyDataBadge mydata={p} /></td>
                      <td className="px-5 py-2 font-mono text-xs">{p.mark || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="erp-card overflow-hidden" data-testid="pending-purchases-card">
            <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800"><h3 className="font-heading font-semibold">{t("pending_purchases")} · {data.pending_purchases.length}</h3></div>
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {data.pending_purchases.map((d) => (
                <Link key={d.id} to={`/documents/${d.id}`} className="px-5 py-3 flex items-center justify-between text-sm hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`pending-purchase-${d.id}`}>
                  <span><span className="font-mono font-medium text-blue-800 dark:text-blue-300">{d.number}</span><span className="text-slate-500 ml-2">{d.contact_name}</span></span>
                  <span className="font-mono">{fmtMoney(d.total)}</span>
                </Link>
              ))}
              {data.pending_purchases.length === 0 && <div className="px-5 py-8 text-center text-sm text-slate-500">{t("no_results")}</div>}
            </div>
          </div>
        </div>
      </div>

      <IncomingDocs onChanged={load} />

      <div className="erp-card overflow-hidden" data-testid="mydata-log-card">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800"><h3 className="font-heading font-semibold">{t("mydata_log")}</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm erp-table">
            <thead><tr className="text-left"><th className="px-5 py-2">{t("date")}</th><th className="px-5 py-2">{t("operation")}</th><th className="px-5 py-2">{t("number")}</th><th className="px-5 py-2">{t("status")}</th><th className="px-5 py-2">{t("mark")}</th><th className="px-5 py-2"></th></tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {log.map((l) => (
                <tr key={l.id} data-testid={`mydata-log-row-${l.id}`}>
                  <td className="px-5 py-2 font-mono text-slate-500">{fmtDateTime(l.created_at)}</td>
                  <td className="px-5 py-2 font-mono">{l.operation}</td>
                  <td className="px-5 py-2 font-mono">{l.ref_label}</td>
                  <td className="px-5 py-2">
                    <span className={cn("text-xs font-semibold uppercase tracking-wider", l.status === "success" ? "text-emerald-700 dark:text-emerald-300" : "text-red-600")}>
                      {l.status === "success" ? t("md_status_sent") : t("md_status_error")}{l.mode === "mock" ? " · MOCK" : ""}
                    </span>
                    {l.errors?.length > 0 && <div className="text-xs text-red-600 font-mono">{l.errors[0].message}</div>}
                  </td>
                  <td className="px-5 py-2 font-mono text-xs">{l.mark || "—"}</td>
                  <td className="px-5 py-2 text-right">
                    {l.xml !== null && l.operation !== "CancelInvoice" && (
                      <a href={mydataApi.logXmlUrl(l.id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-700 dark:text-blue-300 hover:underline" data-testid={`mydata-log-xml-${l.id}`}><FileCode2 size={12} />XML</a>
                    )}
                  </td>
                </tr>
              ))}
              {log.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500" data-testid="mydata-log-empty">{t("no_results")}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
